import { useEffect, useRef, useState } from "react";
import { IconRosette, IconArrowsShuffle } from "@tabler/icons-react";
import Card from "./Card";
import styles from "../TalentProfile.module.css";
import Button from "../../../../components/UI/Button/button";

const R = 32;
const CIRC = 2 * Math.PI * R;

export default function MatchRatingCard({
  matchPct,
  matchStars,
  onMatchClick,
  disabled,
  shortlisted,
  onMatchWithTalent,
}) {
  const [offset, setOffset] = useState(CIRC);
  const [displayPct, setDisplayPct] = useState(0);
  const rafRef = useRef(null);

  const ringColor =
    matchPct >= 70 ? "#10b981" : matchPct >= 45 ? "#d97706" : "#ef4444";
  const matchLabel =
    matchPct >= 70
      ? "Strong Match"
      : matchPct >= 45
        ? "Good Match"
        : "Partial Match";
  const matchDesc =
    matchPct >= 70
      ? "This talent closely fits your active job requirements."
      : matchPct >= 45
        ? "Reasonable fit — worth a screening conversation."
        : "Limited overlap with your current open roles.";

  useEffect(() => {
    const delay = setTimeout(() => {
      setOffset(CIRC - (matchPct / 100) * CIRC);

      const dur = 900;
      let start = null;
      function countUp(ts) {
        if (!start) start = ts;
        const prog = Math.min((ts - start) / dur, 1);
        setDisplayPct(Math.round(prog * matchPct));
        if (prog < 1) rafRef.current = requestAnimationFrame(countUp);
      }
      rafRef.current = requestAnimationFrame(countUp);
    }, 120);

    return () => {
      clearTimeout(delay);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [matchPct]);

  return (
    <Card
      icon={IconRosette}
      title="Match Rating"
      subtitle="Against your active roles"
    >
      <div className={styles.ratingTop}>
        <div className={styles.ratingRingWrap}>
          <svg viewBox="0 0 80 80">
            <circle className={styles.ratingRingBg} cx="40" cy="40" r={R} />
            <circle
              className={styles.ratingRingFill}
              cx="40"
              cy="40"
              r={R}
              stroke={ringColor}
              strokeDasharray={CIRC.toFixed(2)}
              strokeDashoffset={offset.toFixed(2)}
            />
          </svg>
          <div className={styles.ratingRingLabel}>
            <span className={styles.ratingPct}>{displayPct ?? 0}</span>
            <span className={styles.ratingPctSign}>%</span>
          </div>
        </div>
        <div className={styles.ratingRight}>
          <div className={styles.ratingStars}>
            {[1, 2, 3, 4, 5].map((i) => (
              <span
                key={i}
                className={`${styles.ratingStar} ${i <= matchStars ? styles.ratingStarFilled : ""}`}
              >
                ★
              </span>
            ))}
          </div>
          <div className={styles.ratingLabel}>{matchLabel}</div>
          <div className={styles.ratingDesc}>{matchDesc}</div>
        </div>
      </div>
      {shortlisted ? (
        <Button
          className={styles.matchBtn}
          onClick={onMatchWithTalent}
          leftIcon={<IconArrowsShuffle size={17} />}
          loading={disabled}
          disabled={disabled}
        >
          Match with talent
        </Button>
      ) : (
        <Button
          className={styles.matchBtn}
          onClick={onMatchClick}
          loading={disabled}
          disabled={disabled}
          leftIcon={<IconArrowsShuffle size={17} />}
        >
          Match
        </Button>
      )}
    </Card>
  );
}
